import { Link } from "react-router";

import type { ContactInfoQuery } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants/external-link";

import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithBackGround,
} from "@/components/features/_layouts";

import { InputTextFieldNew } from "@/components/ui/form/fields-new/input-field/input-text";

import { Form } from "./form";
import { HeaderRightButton } from "./header-right-button";
import { useContactForm } from "../../hooks";

const DOUBLE_CHARGE_FAQ_URL = `${externalLink.faqSite}/faq/guest/payment/882fffe15c57820db34a81ab1f6a814f`;

type Props = {
	hotelID: string;
	data: ContactInfoQuery;
};

export const ContactNewForm = ({ hotelID, data }: Props) => {
	const { form } = useContactForm({ hotelID });
	const {
		register,
		formState: { errors },
	} = form;

	const hotelEmailAddress = data.hotel.email ?? "登録されていません。施設設定ページで設定してください。";

	return (
		<FeatureIslandContainer>
			<FeatureIslandHeader title="問い合わせ" rightChildren={<HeaderRightButton form={form} />} />
			<FeatureIslandMainWithBackGround>
				<ul className="list-disc space-y-2 pl-5 text-sm text-zinc-900">
					<li>【返答期間】<span className="text-brand">月曜〜金曜 10:00-17:00</span> ※年末年始を除く</li>
					<li>土日にいただいたお問い合わせへは、月曜日より順次ご返答を差し上げます。</li>
					<li>
						<Link
							to={externalLink.faqSite}
							className="text-link-400 underline-offset-4 hover:underline"
							target="_blank"
							viewTransition
						>
							サポートページ
						</Link>
						内のマニュアルをご覧いただくことで、早期解決ができる場合がございますので、お問い合わせの前にご一読ください。
					</li>
					<li>
						決済に関する問い合わせが増えております。まずは下記をご確認ください。
						<br />
						<Link
							to={DOUBLE_CHARGE_FAQ_URL}
							className="text-link-400 underline-offset-4 hover:underline"
							target="_blank"
							viewTransition
						>
							キャンセルしていないのに返金があった / 2回引き落としがあった
						</Link>
					</li>
				</ul>

				<section className="flex flex-col gap-y-12" aria-labelledby="contact-reply-mail-heading">
					<div className="space-y-4">
						<h2 id="contact-reply-mail-heading" className="text-sm font-medium text-zinc-900">
							返信先
						</h2>

						<div className="space-y-1.5">
							<p className="text-xs font-medium text-zinc-700">施設の登録メール</p>
							<p className="break-all text-sm text-zinc-900">{hotelEmailAddress}</p>
							<p className="text-xs leading-relaxed text-zinc-500">
								別の宛先を指定しない場合、返信はこのアドレスに届きます。
							</p>
						</div>
					</div>

					<InputTextFieldNew
						label={
							<div className="space-y-1">
								<p className="text-sm font-medium text-zinc-900">別の返信先（任意）</p>
								<p className="text-xs font-normal leading-relaxed text-zinc-500">
									入力した場合のみ、こちら宛に返信します。
								</p>
							</div>
						}
						type="email"
						autoComplete="email"
						placeholder="（例）support@example.com"
						errorMessage={errors.hostReplyToMail?.message}
						{...register("hostReplyToMail")}
					/>
				</section>

				<Form form={form} />
			</FeatureIslandMainWithBackGround>
		</FeatureIslandContainer>
	);
};

ContactNewForm.displayName = "ContactNewForm";
